/**
 * Monte Carlo Simulation Engine for LifeMath
 *
 * Models realistic portfolio allocation that shifts from equities to bonds
 * as you age (glide path), applying appropriate returns and volatility.
 */

// Asset class parameters (nominal returns and volatility)
const ASSET_CLASSES = {
  stocks: { mean: 0.10, stdDev: 0.18 },  // S&P 500 historical ~10% return, ~18% volatility
  bonds: { mean: 0.04, stdDev: 0.05 }    // Aggregate bonds ~4% return, ~5% volatility
};

/**
 * Calculate stock allocation based on age using common glide path
 * Rule of thumb: 110 - age = stock percentage (capped at 90% max, 20% min)
 */
function getStockAllocation(age) {
  const stockPercent = Math.max(20, Math.min(90, 110 - age));
  return stockPercent / 100;
}

/**
 * Get portfolio parameters (mean return and volatility) for a given age
 * Based on stock/bond allocation glide path
 *
 * IMPORTANT: Uses years-retired approach to match baseline model:
 * - Full stock allocation (10% mean) during accumulation phase
 * - Gradual shift to bonds during retirement based on years retired
 * This matches the baseline retirement age calculation logic.
 */
function getPortfolioParams(age, retireAge = 65) {
  let stockAlloc, mean;

  if (age < retireAge) {
    // Before retirement: maintain aggressive growth allocation
    // Use 90% stocks for growth (matches baseline 10% return assumption)
    stockAlloc = 0.90;
    mean = 0.10; // Match baseline model's accumulation phase return
  } else {
    // After retirement: gradually reduce risk based on years retired
    const yearsRetired = age - retireAge;

    if (yearsRetired < 10) {
      // First 10 years of retirement: 60% stocks (8.5% mean)
      stockAlloc = 0.60;
      mean = 0.085;
    } else if (yearsRetired < 20) {
      // Years 10-20 of retirement: 40% stocks (7% mean)
      stockAlloc = 0.40;
      mean = 0.07;
    } else {
      // 20+ years retired: 30% stocks (6% mean)
      stockAlloc = 0.30;
      mean = 0.06;
    }
  }

  const bondAlloc = 1 - stockAlloc;

  // Portfolio volatility based on stock/bond allocation
  // Using sqrt of weighted variances with 0.2 correlation between stocks/bonds
  const correlation = 0.2;
  const stockVar = Math.pow(ASSET_CLASSES.stocks.stdDev, 2);
  const bondVar = Math.pow(ASSET_CLASSES.bonds.stdDev, 2);
  const covariance = correlation * ASSET_CLASSES.stocks.stdDev * ASSET_CLASSES.bonds.stdDev;
  const portfolioVar = Math.pow(stockAlloc, 2) * stockVar +
                       Math.pow(bondAlloc, 2) * bondVar +
                       2 * stockAlloc * bondAlloc * covariance;
  const stdDev = Math.sqrt(portfolioVar);

  return { mean, stdDev, stockAlloc };
}

/**
 * Box-Muller transform to generate normally distributed random numbers
 */
function randomNormal(mean = 0, stdDev = 1) {
  let u1 = Math.random();
  let u2 = Math.random();
  while (u1 === 0) u1 = Math.random();
  const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
  return z0 * stdDev + mean;
}

/**
 * Run retirement-period-only simulations starting from retirement age
 * This calculates "Given I retire with X dollars, will it last until age Y?"
 */
function runRetirementOnlySimulations(startingPortfolio, startAge, endAge, vision, baselineData, numSimulations) {
  const simulations = [];

  for (let sim = 0; sim < numSimulations; sim++) {
    const simResult = [];
    let portfolio = startingPortfolio;

    for (let age = startAge; age <= endAge; age++) {
      // Find the corresponding year in baseline data to get cash flows
      const baselineYear = baselineData.find(d => d.age === age);
      const cashFlow = baselineYear ? (baselineYear.netCashFlow || 0) : 0;

      // Get age-appropriate portfolio parameters
      const portfolioParams = getPortfolioParams(age, startAge); // startAge is retireAge in this context

      // Generate random return
      const randomReturn = randomNormal(portfolioParams.mean, portfolioParams.stdDev);

      // Apply return and cash flow
      portfolio = portfolio * (1 + randomReturn) + cashFlow;
      portfolio = Math.max(0, portfolio); // Floor at 0

      simResult.push({
        age,
        portfolio: Math.round(portfolio)
      });

      // Stop if out of money
      if (portfolio <= 0) break;
    }

    simulations.push(simResult);
  }

  return simulations;
}

/**
 * Run Monte Carlo simulation using baseline data from main app
 * @param {Array} baselineData - The financial projection from calculateFinancials()
 * @param {Object} options - Simulation options
 * @returns {Object} Simulation results with percentiles and statistics
 */
export function runMonteCarloSimulation(baselineData, vision, options = {}) {
  const {
    numSimulations = 1000
  } = options;

  if (!baselineData || baselineData.length === 0) {
    console.error('Monte Carlo: No baseline data provided');
    return null;
  }

  const retireAge = vision?.retireAge || 65;
  const startAge = baselineData[0]?.age || 30;

  // Log allocation strategy for debugging
  console.log('Monte Carlo with years-retired glide path (matches baseline):');
  console.log(`  Before retirement (age ${startAge}-${retireAge - 1}): 90% stocks, 10% mean return`);
  console.log(`  Retirement years 0-9 (age ${retireAge}-${retireAge + 9}): 60% stocks, 8.5% mean return`);
  console.log(`  Retirement years 10-19 (age ${retireAge + 10}-${retireAge + 19}): 40% stocks, 7% mean return`);
  console.log(`  Retirement years 20+ (age ${retireAge + 20}+): 30% stocks, 6% mean return`);

  // Run simulations
  const simulations = [];

  for (let sim = 0; sim < numSimulations; sim++) {
    const simResult = [];

    for (let i = 0; i < baselineData.length; i++) {
      const year = baselineData[i];
      const age = year.age;

      let adjustedPortfolio;
      if (i === 0) {
        // First year: start with baseline
        adjustedPortfolio = year.portfolioValue || 0;
      } else {
        const prevSimPortfolio = simResult[i - 1].portfolio;

        // Get age-appropriate portfolio parameters (glide path)
        // Pass retireAge so returns can be reduced during retirement (matching baseline)
        const portfolioParams = getPortfolioParams(age, retireAge);

        // Use the net cash flow directly from baseline data
        // This represents contributions (positive) or withdrawals (negative)
        const cashFlow = year.netCashFlow || 0;

        // Generate random return for this year using age-based allocation
        const randomReturn = randomNormal(portfolioParams.mean, portfolioParams.stdDev);

        // Apply random return to previous simulation portfolio, then add cash flow
        adjustedPortfolio = prevSimPortfolio * (1 + randomReturn) + cashFlow;
      }

      // Floor at 0
      adjustedPortfolio = Math.max(0, adjustedPortfolio);

      simResult.push({
        age,
        portfolio: Math.round(adjustedPortfolio)
      });
    }

    simulations.push(simResult);
  }

  // Calculate percentiles
  const percentileData = calculatePercentiles(simulations, [10, 25, 50, 75, 90]);

  // Find median at retirement (needed for retirement-period simulations)
  // Round retireAge to find the closest year in percentileData (handles fractional ages like 61.53)
  const retireAgeRounded = Math.ceil(retireAge); // Use ceiling to be conservative
  const retirementYear = percentileData.find(d => d.age === retireAgeRounded);
  const medianAtRetirement = retirementYear ? retirementYear.p50 : 0;

  console.log(`🎯 Monte Carlo retirement check: retireAge=${retireAge}, rounded=${retireAgeRounded}, median portfolio=$${Math.round(medianAtRetirement/1000)}k`);

  // Run NEW simulations starting FROM retirement age with median portfolio value
  // This gives us "retirement success rate" - given you reach retirement with X saved, will it last?
  const retirementSimulations = runRetirementOnlySimulations(
    medianAtRetirement,
    retireAgeRounded, // Use rounded age for simulation lookups
    Math.max(90, retireAgeRounded + 30), // Simulate to at least age 90 or 30 years post-retirement
    vision,
    baselineData,
    numSimulations
  );

  // Calculate success rates from retirement-period-only simulations
  const successAt85 = calculateSuccessRate(retirementSimulations, 85);
  const successAt90 = calculateSuccessRate(retirementSimulations, 90);

  // Find worst case (10th percentile at retirement + 20 years)
  const targetAge = Math.min(retireAge + 20, 90);
  const worstCaseYear = percentileData.find(d => d.age === targetAge);
  const worstCase = worstCaseYear ? worstCaseYear.p10 : 0;

  // Get starting portfolio from baseline
  const startingPortfolio = baselineData[0]?.portfolioValue || 0;
  const endAge = baselineData[baselineData.length - 1]?.age || 90;

  return {
    percentileData,
    statistics: {
      numSimulations,
      successRateAt85: successAt85,
      successRateAt90: successAt90,
      worstCase10thPercentile: worstCase,
      medianAtRetirement
    },
    parameters: {
      startAge,
      endAge,
      retireAge,
      startingPortfolio,
      // Include glide path info
      startingStockAllocation: Math.round(getStockAllocation(startAge) * 100),
      retirementStockAllocation: Math.round(getStockAllocation(retireAge) * 100)
    }
  };
}

/**
 * Calculate percentiles from simulation results
 */
function calculatePercentiles(simulations, percentiles = [10, 25, 50, 75, 90]) {
  if (simulations.length === 0) return [];

  const numYears = simulations[0].length;
  const results = [];

  for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
    const yearValues = simulations
      .map(sim => sim[yearIndex].portfolio)
      .sort((a, b) => a - b);

    const age = simulations[0][yearIndex].age;
    const yearResult = { age };

    percentiles.forEach(p => {
      // Nearest rank method: for the p-th percentile, take the value at index ceil(p/100 * n) - 1
      // This ensures p=50 on 1000 values gives index 499 (500th value), p=90 gives index 899, etc.
      const index = Math.max(0, Math.ceil((p / 100) * yearValues.length) - 1);
      yearResult[`p${p}`] = yearValues[index];
    });

    yearResult.mean = Math.round(
      yearValues.reduce((sum, v) => sum + v, 0) / yearValues.length
    );

    results.push(yearResult);
  }

  return results;
}

/**
 * Calculate success rate (percentage of simulations that don't run out of money)
 * A simulation succeeds if it has the target age AND has money at that age
 * If a simulation ends before the target age (ran out of money), it's a failure
 */
function calculateSuccessRate(simulations, targetAge) {
  const successCount = simulations.filter(sim => {
    // Check if simulation reached the target age
    const yearAtTarget = sim.find(year => year.age === targetAge);

    // Success = reached target age AND still has money
    // If simulation ended early (no entry for targetAge), it's a failure
    return yearAtTarget && yearAtTarget.portfolio > 0;
  }).length;

  return Math.round((successCount / simulations.length) * 100);
}

/**
 * Format currency for display
 */
export function formatCurrency(value) {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value.toFixed(0)}`;
}
